import React, { useEffect, useState } from 'react';
import {
  Grid,
  Button,
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Snackbar,
  Alert,
  Paper,
  alpha,
  Chip,
  Divider,
  Link,
  CircularProgress,
  Container,
  Avatar,
  Tooltip,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import LockIcon from '@mui/icons-material/Lock';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import { 
  getUserImages, 
  enterCompetition, 
  hasUserEnteredCompetition, 
  getCompetitions, 
  removeCompetitionEntry,
  getCompetitionEntry,
  getUserImagesWithCompetitionStatus
} from 'src/apiCalls';
import API_URL from 'src/config';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import logger from 'src/logger';
import { Image, Competition } from 'src/interfaces';

const EnterCompetitionPage: React.FC = () => {
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [hasEntered, setHasEntered] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [currentPhase, setCurrentPhase] = useState<'entry' | 'voting' | 'ended'>('entry');
  const [countdown, setCountdown] = useState('');
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [isCompetitionLoading, setIsCompetitionLoading] = useState(true);
  const navigate = useNavigate();
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [imageToRemove, setImageToRemove] = useState<string | null>(null);

  const { user } = useUserContext();
  const { accessToken, uid } = user || {};
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const competitionId = queryParams.get('id');

  // Effect to fetch competition details
  useEffect(() => {
    const fetchCompetitionDetails = async () => {
      if (!competitionId) {
        setError('No competition ID provided');
        setIsCompetitionLoading(false);
        return;
      }

      try {
        setIsCompetitionLoading(true);
        const competitions = await getCompetitions();
        const selectedCompetition = competitions.find(c => c.id === competitionId);
        
        if (selectedCompetition) {
          setCompetition(selectedCompetition);
          // Set current phase based on date of month
          const now = new Date();
          const dayOfMonth = now.getDate();
          
          // Entry phase is from 1st to 14th of the month
          if (dayOfMonth >= 1 && dayOfMonth <= 14) {
            setCurrentPhase('entry');
          } else {
            setCurrentPhase('voting');
          }
        } else {
          setError('Competition not found');
        }
      } catch (err) {
        logger.error('Error fetching competition details:', err);
        setError('Failed to load competition details');
      } finally {
        setIsCompetitionLoading(false);
      }
    };

    fetchCompetitionDetails();
  }, [competitionId]);

  // Effect for Countdown Timer
  useEffect(() => {
    if (!competition) return;

    const updateCountdown = () => {
      const now = new Date();
      const endDate = new Date(competition.endDate);
      
      if (now > endDate) {
        setCountdown('Competition has ended');
        return;
      }
      
      const diff = endDate.getTime() - now.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      
      setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s until competition ends`);
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(timer);
  }, [competition]);

  useEffect(() => {
    const fetchData = async () => {
      if (!accessToken || !uid || !competitionId) {
        setError('You must be logged in to view your catch reports.');
        setLoading(false);
        return;
      }

      try {
        // Get user's images with competition entry status
        const imagesData = await getUserImagesWithCompetitionStatus(accessToken, uid, competitionId);
        
        // Check if any image is entered in this competition
        const hasEnteredImage = imagesData.some(img => img.enteredCompetition === true);
        
        setImages(imagesData);
        setHasEntered(hasEnteredImage);
      } catch (err) {
        logger.error('Error fetching data:', err);
        setError('Could not load your catch reports or entry status.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken, uid, competitionId]);

  const handleEnterCompetition = async (imageId: string) => {
    if (!competition) return;
    
    if (currentPhase !== 'entry') {
      setError('Entries are only accepted during the entry phase.');
      setSnackbarMessage('Entries are only accepted during the entry phase.');
      setSnackbarOpen(true);
      return;
    }
    
    if (!accessToken || !uid) {
      setError('You must be logged in to enter the competition.');
      return;
    }

    // Check if this specific image is already entered
    const imageToEnter = images.find(img => img.id === imageId);
    if (imageToEnter?.enteredCompetition) {
      setError('This image has already been entered in the competition.');
      setSnackbarMessage('This image has already been entered in the competition.');
      setSnackbarOpen(true);
      return;
    }

    // Check if user has entered any other image
    if (hasEntered) {
      setError('You have already entered another catch in this competition.');
      setSnackbarMessage('You have already entered another catch in this competition.');
      setSnackbarOpen(true);
      return;
    }

    try {
      await enterCompetition(accessToken, uid, imageId, competition.id);

      // Update the entered status for this specific image
      setImages((prevImages) =>
        prevImages.map((img) =>
          img.id === imageId 
            ? { ...img, enteredCompetition: true } 
            : { ...img, enteredCompetition: false }
        )
      );
      setHasEntered(true);
      setSnackbarMessage('Your catch has been successfully entered into the competition!');
      setSnackbarOpen(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/competitions/view?id=${competition.id}`);
      }, 2000);
    } catch (err) {
      logger.error('Error entering competition:', err);
      setError('Failed to enter the competition. Try again later.');
      setSnackbarMessage('Failed to enter the competition. Try again later.');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleRemoveClick = (imageId: string) => {
    setImageToRemove(imageId);
    setShowRemoveDialog(true);
  };

  const handleCloseRemoveDialog = () => {
    setShowRemoveDialog(false);
    setImageToRemove(null);
  };

  const handleRemoveEntry = async (imageId: string) => {
    if (!competition || !accessToken || !uid) return;

    if (currentPhase !== 'entry') {
      setSnackbarMessage('Entries can only be removed during the entry phase.');
      setSnackbarOpen(true);
      return;
    }

    try {
      await removeCompetitionEntry(accessToken, uid, imageId, competition.id);

      // Update the local state
      setImages((prevImages) =>
        prevImages.map((img) =>
          img.id === imageId ? { ...img, enteredCompetition: false } : img
        )
      );
      setHasEntered(false);
      setSnackbarMessage('Your entry has been removed from the competition.');
      setSnackbarOpen(true);
    } catch (err) {
      logger.error('Error removing competition entry:', err);
      setError('Failed to remove the competition entry. Please try again later.');
      setSnackbarMessage('Failed to remove the competition entry. Please try again later.');
      setSnackbarOpen(true);
    } finally {
      setShowRemoveDialog(false);
      setImageToRemove(null);
    }
  };

  if (isCompetitionLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Competition...</Typography>
      </Box>
    );
  }

  if (!competition) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error || 'Competition not found'}
        </Alert>
        <Button 
          component={RouterLink} 
          to="/competitions" 
          variant="contained" 
          color="primary"
        >
          Back to Competitions
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Enter Competition</title>
      </Helmet>
      <PageTitleWrapper>
        <PageTitle
          heading="Enter Competition"
          subHeading="Select a catch report to enter into the competition"
        />
      </PageTitleWrapper>
      <Container maxWidth="lg">
        {isCompetitionLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                component={RouterLink}
                to="/images/addsingle"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
              >
                Add New Catch Report
              </Button>
              {competition && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" color="text.secondary">
                    {countdown}
                  </Typography>
                  <Chip
                    label={currentPhase === 'entry' ? 'Entry Phase' : 'Voting Phase'}
                    color={currentPhase === 'entry' ? 'success' : 'info'}
                    sx={{ 
                      mt: 1,
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: '2px'
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Select a catch to enter in the competition
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Choose one of your catches to enter in the {competition.title} competition.
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Box>

            {loading ? (
              <Typography>Loading your catch reports...</Typography>
            ) : (
              <Grid container spacing={3}>
                {images.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body1" align="center" sx={{ my: 4 }}>
                      You don't have any catches to enter. Upload a catch report first!
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      <Button 
                        variant="contained" 
                        component={RouterLink} 
                        to="/images/addsingle"
                      >
                        Upload a Catch
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  images.map((img) => (
                    <Grid item xs={12} sm={6} md={4} key={img.id}>
                      <Card 
                        sx={{ 
                          borderRadius: '2px',
                          boxShadow: 2,
                          position: 'relative',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          '&:hover': {
                            transform: currentPhase === 'entry' && !hasEntered && !img.enteredCompetition ? 'translateY(-4px)' : 'none',
                            boxShadow: currentPhase === 'entry' && !hasEntered && !img.enteredCompetition ? 4 : 2,
                          }
                        }}
                      >
                        {/* Entry phase lockout overlay */}
                        {currentPhase !== 'entry' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              width: '100%', 
                              height: '100%', 
                              backgroundColor: 'rgba(0,0,0,0.5)', 
                              zIndex: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              color: 'white',
                              borderRadius: '2px'
                            }}
                          >
                            <LockIcon sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h6" align="center">
                              Entry Phase Closed
                            </Typography>
                            <Typography variant="body2" align="center" sx={{ px: 2 }}>
                              Entries are only accepted during 1st-14th of the month
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Already entered badge */}
                        {img.enteredCompetition && (
                          <Chip 
                            label="Entered" 
                            color="success" 
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              zIndex: 3,
                              fontWeight: 'bold',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              borderRadius: '2px'
                            }} 
                          />
                        )}
                        
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={img.path}
                            alt={img.species || 'Catch Image'}
                            sx={{ 
                              objectFit: 'cover',
                              borderRadius: '2px 2px 0 0'
                            }}
                          />
                          {/* Entry Indicator */}
                          {img.enteredCompetition && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2
                              }}
                            >
                              <Tooltip title="Entered in Competition">
                                <Avatar
                                  sx={{
                                    bgcolor: 'success.main',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '2px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  <EmojiEventsIcon fontSize="small" />
                                </Avatar>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="h6">
                            {img.species || 'Unknown Species'}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {img.tags || ''}
                          </Typography>
                          <Typography variant="body2">
                            Weight: <strong>{img.weight}</strong>
                          </Typography>
                          
                          {/* Button displayed only during entry phase when no entry exists */}
                          {currentPhase === 'entry' && !hasEntered && !img.enteredCompetition ? (
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              sx={{ 
                                borderRadius: '2px',
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                                }
                              }}
                              onClick={() => handleEnterCompetition(img.id)}
                            >
                              Enter Competition
                            </Button>
                          ) : img.enteredCompetition ? (
                            <>
                              <Typography variant="body2" color="success.main" sx={{ mt: 2, fontWeight: 'bold', textAlign: 'center' }}>
                                This catch is entered in the competition
                              </Typography>
                              {currentPhase === 'entry' ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  fullWidth
                                  startIcon={<DeleteIcon />}
                                  sx={{ mt: 1 }}
                                  onClick={() => handleRemoveClick(img.id)}
                                >
                                  Remove Entry
                                </Button>
                              ) : (
                                <ButtonGroup variant="contained" fullWidth sx={{ mt: 1 }}>
                                  <Tooltip title={currentPhase !== 'voting' ? "Voting begins on the 15th of the month" : ""}>
                                    <span>
                                      <Button
                                        component={RouterLink}
                                        to={currentPhase === 'voting' ? `/competitions/vote?id=${competitionId}` : '#'}
                                        startIcon={<HowToVoteIcon />}
                                        disabled={currentPhase !== 'voting'}
                                        sx={{
                                          '&.Mui-disabled': {
                                            backgroundColor: 'action.disabledBackground',
                                            color: 'action.disabled'
                                          }
                                        }}
                                      >
                                        Vote
                                      </Button>
                                    </span>
                                  </Tooltip>
                                  <Button
                                    component={RouterLink}
                                    to={`/competitions/leaderboard?id=${competitionId}`}
                                    startIcon={<EmojiEventsIcon />}
                                  >
                                    Leaderboard
                                  </Button>
                                </ButtonGroup>
                              )}
                            </>
                          ) : hasEntered ? (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic', textAlign: 'center' }}>
                                You've already entered another catch
                              </Typography>
                              {currentPhase === 'voting' && (
                                <ButtonGroup variant="contained" fullWidth sx={{ mt: 1 }}>
                                  <Tooltip title={currentPhase !== 'voting' ? "Voting begins on the 15th of the month" : ""}>
                                    <span>
                                      <Button
                                        component={RouterLink}
                                        to={currentPhase === 'voting' ? `/competitions/vote?id=${competitionId}` : '#'}
                                        startIcon={<HowToVoteIcon />}
                                        disabled={currentPhase !== 'voting'}
                                        sx={{
                                          '&.Mui-disabled': {
                                            backgroundColor: 'action.disabledBackground',
                                            color: 'action.disabled'
                                          }
                                        }}
                                      >
                                        Vote
                                      </Button>
                                    </span>
                                  </Tooltip>
                                  <Button
                                    component={RouterLink}
                                    to={`/competitions/leaderboard?id=${competitionId}`}
                                    startIcon={<EmojiEventsIcon />}
                                  >
                                    Leaderboard
                                  </Button>
                                </ButtonGroup>
                              )}
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic', textAlign: 'center' }}>
                                Entry phase is over
                              </Typography>
                              {currentPhase === 'voting' && (
                                <ButtonGroup variant="contained" fullWidth sx={{ mt: 1 }}>
                                  <Tooltip title={currentPhase !== 'voting' ? "Voting begins on the 15th of the month" : ""}>
                                    <span>
                                      <Button
                                        component={RouterLink}
                                        to={currentPhase === 'voting' ? `/competitions/vote?id=${competitionId}` : '#'}
                                        startIcon={<HowToVoteIcon />}
                                        disabled={currentPhase !== 'voting'}
                                        sx={{
                                          '&.Mui-disabled': {
                                            backgroundColor: 'action.disabledBackground',
                                            color: 'action.disabled'
                                          }
                                        }}
                                      >
                                        Vote
                                      </Button>
                                    </span>
                                  </Tooltip>
                                  <Button
                                    component={RouterLink}
                                    to={`/competitions/leaderboard?id=${competitionId}`}
                                    startIcon={<EmojiEventsIcon />}
                                  >
                                    Leaderboard
                                  </Button>
                                </ButtonGroup>
                              )}
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            )}
          </>
        )}
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarMessage.includes('Failed') ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={showRemoveDialog}
        onClose={handleCloseRemoveDialog}
        aria-labelledby="remove-entry-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '2px',
            p: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle id="remove-entry-dialog-title">
          Remove Competition Entry?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this entry from the competition? This action will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>
              <Typography>Remove your catch from the competition</Typography>
            </li>
            <li>
              <Typography>Cancel any votes received</Typography>
            </li>
            <li>
              <Typography>Allow you to enter a different catch</Typography>
            </li>
          </Box>
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => imageToRemove && handleRemoveEntry(imageToRemove)}
            color="error"
            variant="contained"
          >
            Remove Entry
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnterCompetitionPage;